import { Box, CssBaseline, ThemeProvider } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Outlet } from '@tanstack/react-router';
import { enUS } from 'date-fns/locale/en-US';
import { pl } from 'date-fns/locale/pl';
import { useTranslation } from 'react-i18next';

import LaunchErrorContent from './components/atoms/launch-error-content';
import LaunchErrorOverlay from './components/atoms/launch-error-overlay';
import Loader from './components/atoms/loader/loader';
import { useSettings } from './contexts/settings-context';
import { useAuth } from './features/auth/contexts/auth-provider';
import { useSetInitialOrganization } from './hooks/use-set-initial-organization';
import { useUpdateLanguage } from './hooks/use-update-language';
import { createCustomTheme } from './theme';

const App = () => {
  const { t } = useTranslation(['common']);

  const auth = useAuth();

  const { settings } = useSettings();

  const isLoggedIn = !!auth.session?.access_token;

  const { availableOrganizationsError, isLoadingAvailableOrganizations } =
    useSetInitialOrganization(isLoggedIn);

  const theme = createCustomTheme({
    direction: settings.direction,
    theme: settings.theme,
  });

  useUpdateLanguage();

  const renderContent = () => {
    if (auth.loading) {
      return (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Loader />
        </Box>
      );
    }
    if (isLoggedIn && availableOrganizationsError && !isLoadingAvailableOrganizations) {
      return (
        <LaunchErrorOverlay
          title={t('common:launchError.title')}
          trailingContentContainer={<LaunchErrorContent />}
        />
      );
    } else {
      return <Outlet />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={settings.language === 'pl' ? pl : enUS}>
        <CssBaseline />
        {renderContent()}
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
